import { eventQBus } from "../types/EventQBus";
import NextBestAction from "../nextBestAction/NextBestAction.svelte";
import FacetValueTags from "../facetvaluetags/FacetValueTags.svelte";
import ConfigurableFacet from "../facet/ConfigurableFacet.svelte";
import type { ConfigurableFilterOptions } from "@otto-ec/heureka-components/filter";
import { Assets, setActiveConfiguration } from "@otto-ec/heureka-components/filter";
import { mount } from "svelte";
import type { Facet } from "@otto-ec/heureka-components/facet";
import { mapElements } from "../util/Utils";
import { isMobile } from "../util/Breakpoint";
import { CustomRangeInput } from "./slider/CustomRangeInput";
import { events } from "../events";
import { getActiveVariation } from "../experiment/Experiments";

/*                                                                                                            */
const config: Record<string, ConfigurableFilterOptions> = {
  color: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "grid",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "color",
                titleSize: "100",
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "overflowBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "color",
                titleSize: "75",
              },
            },
          },
        },
      },
    },
  },
  rating: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "radio",
              facetValueLabelConfig: {
                type: "rating",
              },
            },
          },
        },
      },
    },
  },
  retailprice: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "79",
            containsRanges: true,
            facetValueConfig: {
              type: "singleSelectRange",
              facetValueLabelConfig: {
                type: "text",
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "79",
            facetValueConfig: {
              type: "singleSelectRange",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
  interval: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "90",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "90",
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "overflowBox",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
  switch: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "toggle",
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "toggle",
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
    },
  },
  radio: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "radio",
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
    },
  },
  chip: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "grid",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "overflowBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "grid",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
  default: {
    filterConfig: {
      default: {
        facetConfig: {
          facetValueGroupConfig: {
            facetValueConfig: {
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          facetValueGroupConfig: {
            type: "overflowBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                showCount: false,
                size: "75",
              },
            },
          },
        },
      },
    },
  },
};

export function initializeConfigurableFacet(fragment: DocumentFragment) {
  /*                                                                                                       */
  setActiveConfiguration(isMobile() ? "sheetFirstLevel" : "default");
  mapElements(".ts_heureka_svelteFacet", (elem) => createFacet(elem), fragment).map(CustomRangeInput.init);
}

function createFacet(elem: HTMLElement) {
  const parentElement = elem.parentElement as HTMLElement;
  const { serialized, facetVisibility, filterTitleMode, hasNbaValues } = elem.dataset;
  const facet: Facet = JSON.parse(serialized);
  const filterConfig =
    config[facet.id.toLowerCase()] ||
    (facet.selectionType === "range" && config["interval"]) ||
    (facet.selectionType === "switch" && config["switch"]) ||
    (facet.selectionType === "radio" && config["radio"]) ||
    (facet.selectionType === "chip" && config["chip"]) ||
    (facet.selectionType === "rating" && config["rating"]) ||
    (facet.selectionType === "colorTile" && config["color"]) ||
    config["default"];

  mount(ConfigurableFacet, {
    target: parentElement,
    props: {
      data: {
        ...facet,
        showTitle: elem.dataset.showTitle === "true",
        useSlider: elem.dataset.mayHideSlider !== "true" || (facet.id === "retailprice" && isMobile()),
        parentId: `find_filter_${facet.filterId}`,
      },
      filterTitleMode: filterTitleMode,
      visibility: facetVisibility,
      hasNbaValues: hasNbaValues,
      shortenValueList: getActiveVariation("e3607", "StatusQuo") === "ShortList",
      ...filterConfig,
    },
  });
  elem.remove();
  return parentElement;
}

export function initializeNextBestActionComponent() {
  const useNewModelForPriceRanges = getActiveVariation("e3474", "StatusQuo") !== "StatusQuo";

  mapElements(".ts_heureka_svelteNextBestAction", (elem) => {
    const parentElement = elem.parentElement as HTMLElement;
    const { serialized } = elem.dataset;

    /*                                                         */
    const parsedData = JSON.parse(serialized);

    /*                                                                                                             */
    /*                                                                    */
    if (parsedData.values && !useNewModelForPriceRanges && isMobile()) {
      parsedData.values.forEach((value) => {
        if (value.name === "preis-in-eur") {
          value.referencedFacetValueId = "facetValue_retailprice_value";
        }
      });
    }
    mount(NextBestAction, {
      target: parentElement,
      props: {
        ...parsedData,
        containsOnlyNbaValues: elem.dataset.containsOnlyNbaValues === "true",
      },
    });
    elem.remove();
    return parentElement;
  });
}

export function initializeFacetValueTagsComponent() {
  /*                                                                                    */
  const refinementBarPlaceholder = document.getElementById("heureka_refinementBar__placeholder");
  if (
    refinementBarPlaceholder &&
    !refinementBarPlaceholder.classList.contains("heureka_refinementBar__placeholder--hidden")
  ) {
    refinementBarPlaceholder.classList.add("heureka_refinementBar__placeholder--hidden");
  }

  mapElements(".ts_heureka_svelteFacetValueTags", (elem) => {
    const parentElement = elem.parentElement as HTMLElement;
    const { serialized } = elem.dataset;

    const parsedData = JSON.parse(serialized);

    mount(FacetValueTags, {
      target: parentElement,
      props: {
        ...parsedData,
      },
    });
    elem.remove();
    return parentElement;
  });
}

function initializeAssets() {
  mount(Assets, {
    target: document.body,
  });
}

export default function registerSvelteComponentInitialization() {
  if (isMobile()) {
    events.onFilterSectionLoaded(initializeNextBestActionComponent);
  } else {
    events.onFilterSectionLoaded(initializeFacetValueTagsComponent);
  }
  eventQBus.once("heureka.filterSection.loaded", initializeAssets);
}
